import { Injectable } from '@angular/core';
import { Auth, onAuthStateChanged, User } from '@angular/fire/auth';
import { Firestore, doc, onSnapshot } from '@angular/fire/firestore';

@Injectable({
  providedIn: 'root'
})
export class FirestoreWarmupService {
  private authUnsubscribe?: () => void;
  private listenerUnsubscribe?: () => void;
  private activeUserId?: string;

  constructor(
    private auth: Auth,
    private firestore: Firestore,
  ) { }

  init(): void {
    if (this.authUnsubscribe) {
      return;
    }

    this.authUnsubscribe = onAuthStateChanged(this.auth, (user) => this.handleAuthState(user));
  }

  private handleAuthState(user: User | null): void {
    if (!user) {
      this.stopWarmupListener();
      return;
    }

    if (this.activeUserId === user.uid && this.listenerUnsubscribe) {
      return;
    }

    this.stopWarmupListener();
    this.activeUserId = user.uid;

    const currentUserRef = doc(this.firestore, `users/${user.uid}`);
    this.listenerUnsubscribe = onSnapshot(
      currentUserRef,
      () => undefined,
      (error) => {
        console.warn('[FirestoreWarmupService] Firestore warmup listener failed:', error);
        this.listenerUnsubscribe = undefined;
      }
    );
  }

  private stopWarmupListener(): void {
    const unsubscribe = this.listenerUnsubscribe;
    this.listenerUnsubscribe = undefined;
    this.activeUserId = undefined;

    if (!unsubscribe) {
      return;
    }

    try {
      unsubscribe();
    } catch (error) {
      if (!this.isFirestoreUnexpectedStateError(error)) {
        throw error;
      }
    }
  }

  private isFirestoreUnexpectedStateError(error: unknown): boolean {
    return this.getErrors(error).some((err) => err instanceof Error
      && err.message.includes('FIRESTORE')
      && err.message.includes('INTERNAL ASSERTION FAILED: Unexpected state'));
  }

  private getErrors(error: unknown): unknown[] {
    if (error && typeof error === 'object' && 'errors' in error && Array.isArray(error.errors)) {
      return error.errors;
    }

    return [error];
  }
}
